import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { FileText, Upload, Trash2, Download, Lock, FileWarning, Pencil } from 'lucide-react';

const PortalDocuments = ({ meta }) => {
  const [docs, setDocs] = useState([]);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('Resume');
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/internship-portal/documents`);
      setDocs(res.data.documents || []);
      setMissing(res.data.missing_required || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error('File exceeds the 15 MB limit.'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/internship-portal/documents?category=${encodeURIComponent(category)}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded.');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed.'); } finally { setUploading(false); }
  };

  const view = async (doc) => {
    try {
      const res = await axios.get(`${API_URL}/internship-portal/documents/${doc.doc_id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error('Could not open document.'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await axios.delete(`${API_URL}/internship-portal/documents/${id}`); await load(); toast.success('Deleted.'); }
    catch (e) { toast.error('Could not delete.'); }
  };

  const rename = async (doc) => {
    const name = window.prompt('Rename document:', doc.original_filename);
    if (!name || !name.trim() || name.trim() === doc.original_filename) return;
    try { await axios.put(`${API_URL}/internship-portal/documents/${doc.doc_id}/rename`, { original_filename: name.trim() }); await load(); toast.success('Renamed.'); }
    catch (e) { toast.error('Could not rename.'); }
  };

  if (loading) return <div className="py-16 text-center text-gray-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="portal-documents">
      <div className="bg-white rounded-2xl border p-6">
        <h3 className="font-serif text-lg font-bold text-primary mb-1 flex items-center gap-2"><FileText className="w-5 h-5 text-secondary" /> Documents</h3>
        <p className="text-sm text-gray-500 mb-4">Securely store your internship documents. Files are private to your account.</p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs text-gray-500 mb-1 block">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta.document_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <input id="doc-file-input" type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" onChange={onUpload} data-testid="doc-file-input" />
            <Button className="bg-primary text-white w-full sm:w-auto" disabled={uploading} onClick={() => document.getElementById('doc-file-input').click()} data-testid="upload-doc-btn">
              <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading…' : 'Upload Document'}
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">Allowed: PDF, DOC/DOCX, JPG, PNG, WEBP · Max 15 MB.</p>
      </div>

      {missing.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="missing-docs-banner">
          <FileWarning className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">Missing required documents</p>
            <p className="text-sm text-red-600">{missing.join(', ')}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border p-6">
        <h4 className="font-serif text-base font-bold text-primary mb-4">Uploaded Documents ({docs.length})</h4>
        {docs.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-6">No documents uploaded yet.</p>
        ) : (
          <ul className="divide-y">
            {docs.map((d) => (
              <li key={d.doc_id} className="py-3 flex items-center justify-between gap-3" data-testid={`doc-${d.doc_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 truncate">{d.original_filename}</p>
                  <p className="text-xs text-gray-500">{d.category} · {(d.size / 1024).toFixed(0)} KB</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button className="text-gray-400 hover:text-primary p-2" onClick={() => view(d)} data-testid={`view-doc-${d.doc_id}`} title="View / Download"><Download className="w-4 h-4" /></button>
                  <button className="text-gray-400 hover:text-primary p-2" onClick={() => rename(d)} data-testid={`rename-doc-${d.doc_id}`} title="Rename"><Pencil className="w-4 h-4" /></button>
                  <button className="text-gray-400 hover:text-red-600 p-2" onClick={() => remove(d.doc_id)} data-testid={`delete-doc-${d.doc_id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-600">Only you and authorized INeedASocialWorker.com administrators can access these documents. They are never shared with schools, agencies, field instructors, or employers.</p>
      </div>
    </div>
  );
};

export default PortalDocuments;
